import './App.css'
// Global imports
import { lazy, useEffect } from 'react'
import { Switch, Route, Redirect } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
// Local imports
import ToastContainer from 'components/ToastContainer/ToastContainer'
import UpdateNotificationContainer from '../UpdateNotificationContainer/UpdateNotificationContainer'
import { setSessionUser } from 'store/actions/sessionDataActions'
import { cleanAndLogout } from '@utils/session'
import themes from 'styles/themes'
import materialUITheme from 'styles/material'
import { ThemeProvider } from 'styled-components/macro'
import { ThemeProvider as MaterialUIProvider } from '@material-ui/styles'
import GlobalStyles from 'styles/globalStyle'
import ImportRetry from 'components/ImportRetry'
import OnlyDesktop from 'components/OnlyDesktop/OnlyDesktop'
import withAuthRoute from '@wrappers/withAuthRoute'
import { useRouter } from '@hooks/useRouter'
import Snackbar from 'components/Snackbar/Snackbar'
import useSnackbar from 'apollo/hooks/useSnackbar'
// Modals
import ConnectIntegration from '@containers/Modals/ConnectIntegration/ConnectIntegration'
import DisconnectIntegration from '@containers/Modals/DisconnectIntegration/DisconnectIntegration'
import RemoveDialogModal from '@containers/Modals/RemoveDialogModal/RemoveDialogModal'
import ShareMention from '@containers/Modals/ShareMention/ShareMention'
import CreateReviewProductOptions from '@containers/Modals/CreateReviewProductOptions/CreateReviewProductOptions'
import useUserSession from 'api/user/useUserSession'
import { UserSessionRequestError } from 'api/user/service'

import ClientGuard from '@wrappers/ClientGuard'
import * as S from './styles'
import type { ReduxState } from 'store/types'

const LazyLoginView = lazy(() =>
  ImportRetry(() => import('@views/Auth/Login/Login')),
)

const LazyRecoverPassword = lazy(() =>
  ImportRetry(() => import('@views/Auth/RecoverPassword/RecoverPassword')),
)

const LazyResetPassword = lazy(() =>
  ImportRetry(() => import('@views/Auth/ResetPassword/ResetPassword')),
)

const LazyLogoutView = lazy(() =>
  ImportRetry(() => import('@views/Auth/Logout/Logout')),
)

const LazyNotFoundView = lazy(() =>
  ImportRetry(() => import('@views/NotFound/NotFound')),
)

// Components view with Auth
const LazyProductViewLoader = lazy(() =>
  ImportRetry(() => import('@views/Product/ProductView')),
)
const ProductViewWithClient = props => (
  <ClientGuard>
    <LazyProductViewLoader {...props} />
  </ClientGuard>
)
const ProductViewLoaderRoute = withAuthRoute(ProductViewWithClient)

const LazyDashboardView = lazy(() =>
  ImportRetry(() => import('@views/Dashboard/Dashboard')),
)
const DashboardViewWithClient = props => (
  <ClientGuard>
    <LazyDashboardView {...props} />
  </ClientGuard>
)
const DashboardViewRoute = withAuthRoute(DashboardViewWithClient)

const LazyMultitenantView = lazy(() =>
  ImportRetry(() => import('@views/Multitenant/Multitenant')),
)
const MultitenantViewRoute = withAuthRoute(LazyMultitenantView)

const AppRoot = () => {
  const route = useRouter()
  const { snackbarProps } = useSnackbar()
  const dispatch = useDispatch()
  const { i18n } = useTranslation()

  const isLoggedIn = useSelector(
    (state: ReduxState) => state.sessionData.meta.isLoggedIn,
  )

  const { data: user, error: userError } = useUserSession({
    enabled: isLoggedIn,
  })

  const redirect = new URLSearchParams(route.location.search).get('redirect')
  const safeRedirect =
    redirect?.startsWith('/') && !redirect.startsWith('//')
      ? redirect
      : '/clients'

  useEffect(() => {
    if (!isLoggedIn || !user) return

    window.localStorage.setItem('userEmail', user.email)
    dispatch(setSessionUser(user))

    if (user.language) {
      void i18n.changeLanguage(user.language)
    }
  }, [dispatch, i18n, isLoggedIn, user])

  useEffect(() => {
    const isUnauthorized =
      userError instanceof UserSessionRequestError && userError.status === 401

    if (!isUnauthorized) return

    cleanAndLogout()
  }, [userError])

  return (
    <MaterialUIProvider theme={materialUITheme}>
      <ThemeProvider theme={themes.light}>
        <OnlyDesktop>
          <GlobalStyles />
          <S.AppWrapper>
            <Switch>
              <DashboardViewRoute isLoggedIn={isLoggedIn} exact path="/" />
              <MultitenantViewRoute
                isLoggedIn={isLoggedIn}
                exact
                path="/clients"
              />
              <DashboardViewRoute isLoggedIn={isLoggedIn} path="/profile" />
              <Route
                path="/login"
                render={props => {
                  return isLoggedIn ? (
                    <Redirect to={safeRedirect} />
                  ) : (
                    <LazyLoginView {...props} />
                  )
                }}
              />
              <Route
                exact
                path="/password-recovery"
                component={LazyRecoverPassword}
              />
              <Route
                exact
                path="/reset-password"
                component={LazyResetPassword}
              />
              <Route exact path="/create-user" component={LazyResetPassword} />

              <Route path="/logout" component={LazyLogoutView} />

              <ProductViewLoaderRoute
                isLoggedIn={isLoggedIn}
                path="/:slug/:product"
              />
              <Route component={LazyNotFoundView} />
            </Switch>
            <ToastContainer />
            <UpdateNotificationContainer />
            <Snackbar {...snackbarProps} />
            <RemoveDialogModal />
            <ShareMention />
            <ConnectIntegration />
            <DisconnectIntegration />
            <CreateReviewProductOptions />
          </S.AppWrapper>
        </OnlyDesktop>
      </ThemeProvider>
    </MaterialUIProvider>
  )
}

export default AppRoot
